import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { ADMIN_MENU_CONFIG, MENU_CONFIG } from "src/constants/menuConfig";
import { useAppSelector } from "src/redux/hooks";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";

interface MenuProps {
  type: "admin" | "tenant";
  collapsed?: boolean;
  onRequestExpand?: () => void;
}

const Menu: React.FC<MenuProps> = ({
  type = "tenant",
  collapsed = false,
  onRequestExpand,
}) => {
  const [showSubMenuIdx, setShowSubMenuIdx] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const menus = type === "admin" ? ADMIN_MENU_CONFIG : MENU_CONFIG;
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const { plural: warehousePlural } = useWarehouseLabel();

  const normalizePath = (path?: string) => path?.replace(/\/+$/, "") || "";
  const isPathActive = (path?: string) => {
    const normalized = normalizePath(path);
    const current = normalizePath(location.pathname);
    return (
      !!normalized &&
      (current === normalized || current.startsWith(`${normalized}/`))
    );
  };

  const menuList = useMemo(() => {
    // The "Warehouses" submenu item's label follows the tenant's
    // configured terminology (Store/Location/Branch/...) instead of the
    // hardcoded config value — clone rather than mutate, since `menus`'
    // items are shared module-level config objects.
    const relabelWarehouses = (list: any[]) =>
      list.map((menu) => {
        if (!menu?.subMenu?.some((s: any) => s.path === "/app/warehouse"))
          return menu;
        return {
          ...menu,
          subMenu: menu.subMenu.map((s: any) =>
            s.path === "/app/warehouse" ? { ...s, label: warehousePlural } : s
          ),
        };
      });

    const rawList = relabelWarehouses(
      Object.keys(menus).map((menuKey) => menus[menuKey])
    );
    if (type === "admin" || isTenantAdmin) return rawList;

    const permissionEntries = Object.keys(permissions || {});
    const shouldFilter = permissionEntries.length > 0;
    if (!shouldFilter) return rawList;

    // `module` can be a single key or an array — an array means "show this
    // if the role has the permission under ANY of these modules" (e.g.
    // Reports has no single page-level permission; it shows up once a role
    // can view at least one report type).
    const hasPermission = (module?: string | string[], action?: string) => {
      if (!module) return true;
      const modules = Array.isArray(module) ? module : [module];
      return modules.some((m) => {
        const modulePerms = permissions?.[m] || [];
        return action ? modulePerms.includes(action) : modulePerms.length > 0;
      });
    };

    return rawList
      .map((menu: any) => {
        if (menu?.adminOnly) {
          return null;
        }
        if (menu?.subMenu?.length) {
          const filteredSub = menu.subMenu.filter((subMenu: any) => {
            if (subMenu?.adminOnly) return false;
            if (!(subMenu.module || menu.module)) return true;
            return hasPermission(
              subMenu.module || menu.module,
              subMenu.permission || "view"
            );
          });
          if (filteredSub.length === 0) return null;
          return { ...menu, subMenu: filteredSub };
        }
        if (!menu?.module) return menu;
        if (!hasPermission(menu.module, menu.permission || "view")) {
          return null;
        }
        return menu;
      })
      .filter(Boolean);
  }, [menus, permissions, isTenantAdmin, type, warehousePlural]);

  const activeMenuIndex = useMemo(
    () =>
      menuList.findIndex((menu: any) => {
        if (menu?.subMenu?.length > 0) {
          return (
            (menu.matchPaths || []).some((path: string) => isPathActive(path)) ||
            isPathActive(menu.path) ||
            menu.subMenu.some(
              (sub: any) =>
                isPathActive(sub.path) ||
                (sub.matchPaths || []).some((p: string) => isPathActive(p))
            )
          );
        }
        return (
          (menu.matchPaths || []).some((path: string) => isPathActive(path)) ||
          isPathActive(menu.path)
        );
      }),
    [menuList, location.pathname]
  );

  useEffect(() => {
    const activeMenu = menuList[activeMenuIndex];
    if (
      activeMenu?.subMenu?.length > 0 &&
      activeMenuIndex !== -1 &&
      showSubMenuIdx === -1
    ) {
      setShowSubMenuIdx(activeMenuIndex);
    }
  }, [activeMenuIndex, menuList]);

  return (
    <nav className="h-[calc(100vh-77px)] overflow-y-auto py-2">
      {menuList.map((menu: any, index: number) => {
        const Icon = menu.icon;
        const isMenuActive =
          (menu.matchPaths || []).some((path: string) => isPathActive(path)) ||
          isPathActive(menu.path) ||
          (menu?.subMenu || []).some(
            (sub: any) =>
              isPathActive(sub.path) ||
              (sub.matchPaths || []).some((p: string) => isPathActive(p))
          );
        const showExpanded = !collapsed && showSubMenuIdx === index;
        return (
          <div key={`menu-${index}`} className="text-sm mx-2 mb-1">
            <div
              className={collapsed ? "" : "flex items-stretch gap-1.5"}
            >
              {!collapsed && (
                <span
                  className={`w-1 my-0.5 rounded-full transition-colors ${
                    isMenuActive || showExpanded
                      ? "bg-primary-600 dark:bg-primary-400"
                      : "bg-transparent"
                  }`}
                />
              )}
              <button
                title={collapsed ? menu.label : undefined}
                className={`rounded-xl py-2.5 transition ${
                  collapsed ? "w-full px-0 flex justify-center" : "flex-1 px-3"
                } ${
                  isMenuActive || showExpanded
                    ? "bg-primary-50 text-primary-700 font-semibold dark:bg-primary-900/30 dark:text-primary-300"
                    : "hover:bg-gray-50 text-gray-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-primary-400"
                }`}
                onClick={() => {
                  if (menu?.subMenu?.length > 0) {
                    if (collapsed) {
                      onRequestExpand?.();
                      setShowSubMenuIdx(index);
                      return;
                    }
                    setShowSubMenuIdx((prev) => (prev === index ? -1 : index));
                  } else {
                    navigate(menu.path);
                  }
                }}
              >
                <div
                  className={`flex items-center ${
                    collapsed ? "justify-center" : "justify-between w-full"
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <Icon className="inline shrink-0" size={17} />
                    {!collapsed && menu.label}
                  </span>

                  {!collapsed &&
                    menu?.subMenu?.length > 0 &&
                    (showExpanded ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    ))}
                </div>
              </button>
            </div>
            {showExpanded && (
              <div className="ml-[19px] mt-1 pb-1 pl-3.5 flex flex-col gap-1 border-l border-gray-200 dark:border-gray-700">
                {menu?.subMenu?.map((subMenu: any, subMenuIdx: number) => {
                  const isSubMenuActive =
                    isPathActive(subMenu.path) ||
                    (subMenu.matchPaths || []).some((p: string) => isPathActive(p));
                  const SubIcon = subMenu.icon;
                  if (subMenu.comingSoon) {
                    return (
                      <div
                        key={`submenu-${index}-${subMenuIdx}`}
                        title="Coming soon"
                        className="flex items-stretch gap-1.5"
                      >
                        <span className="w-1 my-0.5 rounded-full bg-transparent" />
                        <div className="flex-1 rounded-xl px-3 py-2 flex items-center justify-between text-gray-400 cursor-not-allowed dark:text-gray-600">
                          <span className="flex items-center gap-2.5">
                            {SubIcon && (
                              <SubIcon className="shrink-0" size={15} />
                            )}
                            {subMenu.label}
                          </span>
                          <span className="text-[10px] uppercase tracking-wide bg-gray-100 text-gray-400 rounded-full px-1.5 py-0.5 dark:bg-gray-700 dark:text-gray-500">
                            Soon
                          </span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div
                      key={`submenu-${index}-${subMenuIdx}`}
                      className="group flex items-stretch gap-1.5"
                    >
                      <span
                        className={`w-1 my-0.5 rounded-full transition-colors ${
                          isSubMenuActive
                            ? "bg-primary-600 dark:bg-primary-400"
                            : "bg-transparent group-hover:bg-gray-300 dark:group-hover:bg-gray-600"
                        }`}
                      />
                      <button
                        className={`flex-1 rounded-xl px-3 py-2 text-left flex items-center gap-2.5 transition ${
                          isSubMenuActive
                            ? "bg-primary-50 text-primary-700 font-semibold dark:bg-primary-900/30 dark:text-primary-300"
                            : "text-gray-600 hover:bg-gray-50 hover:text-primary-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-primary-300"
                        }`}
                        onClick={() => navigate(subMenu.path)}
                      >
                        {SubIcon && <SubIcon className="shrink-0" size={15} />}
                        {subMenu.label}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

export default Menu;
